import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { getAllInvoiceArticle } from 'src/shared/services/invoiceArticleService';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/system';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceArticle } from '../models/invoiceArticle';
import { ArticleRegisterSearchFilter } from '../models/articleRegisterSearchFilter';

type InvoiceArticleDataGridProps = {
  refetchDataGrid: boolean;
  setRefetchDataGrid: (refetch: boolean) => void;
  setArticleId: (articleId: number) => void;
  setOpenEditArticle: (openEditArticle: boolean) => void;
  searchFilter: ArticleRegisterSearchFilter;
  searchFilterCallback: (searchFilter: ArticleRegisterSearchFilter) => void;
};

export const InvoiceArticleDataGrid = (props: InvoiceArticleDataGridProps) => {
  const { refetchDataGrid, setRefetchDataGrid, setArticleId, setOpenEditArticle, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<InvoiceArticle[]>([]);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });
  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAllInvoiceArticle(searchFilter);
      const invoiceArticlesResult = extendObjectWithId(result.data.results, 'article_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          const withCategoryFallback = invoiceArticlesResult.map((item) => ({
            ...item,
            articleCategory: item.articleCategory_id === 0 ? t('invoice:articleRegister.other') : item.articleCategory,
          }));
          setRows(withCategoryFallback);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setRefetchDataGrid(false);
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter, setRefetchDataGrid, t]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  useEffect(() => {
    if (refetchDataGrid) {
      fetchData();
    }
  }, [fetchData, refetchDataGrid]);

  const onRowClick = (params) => {
    if (params.row && params.row.article_id) {
      setArticleId(params.row.article_id);
      setOpenEditArticle(true);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('invoice:articleRegister.article'),
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.name || ''}</>;
      },
    },
    {
      field: 'articleNr',
      headerName: t('invoice:articleRegister.articleNumber'),
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.articleNr || ''}</>;
      },
    },
    {
      field: 'articleCategory',
      headerName: t('invoice:articleRegister.category'),
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.articleCategory || ''}</>;
      },
    },
    {
      field: 'price',
      headerName: t('invoice:priceInclVat'),
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        const { price } = params.row;
        return (price && <AmountWithCurrency amount={price} />) || '';
      },
    },
    {
      field: 'priceExcludingVat',
      headerName: t('invoice:priceExclVat'),
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        const { priceExcludingVat } = params.row;
        return priceExcludingVat > 0 ? <AmountWithCurrency amount={priceExcludingVat} /> : '';
      },
    },
    {
      field: 'vatRate',
      headerName: t('invoice:vatAmount'),
      headerAlign: 'right',
      align: 'right',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        const { vatRate } = params.row;
        return vatRate ? `${vatRate}%` : '0%';
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<InvoiceArticle>
        autoHeight
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        selectionModel={[]}
        sortModel={[
          {
            field: searchFilter.sortCol === 'articleCategory_id' ? 'articleCategory' : searchFilter.sortCol,
            sort: searchFilter.sortDir,
          },
        ]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length > 0) {
            const { field } = newSortModel[0];
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: field === 'articleCategory' ? 'articleCategory_id' : field,
              sortDir: newSortModel[0].sort,
            });
          } else {
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: searchFilter.sortCol,
              sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
            });
          }
        }}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
